import { useState } from 'react';
import { X, Copy, Loader2 } from 'lucide-react';

interface CopyEntriesModalProps {
    companyCode: string;
    availableDates: string[];
    onCopy: (sourceDate: string, targetDate: string) => Promise<void>;
    onClose: () => void;
}

export function CopyEntriesModal({ companyCode, availableDates, onCopy, onClose }: CopyEntriesModalProps) {
    const [sourceDate, setSourceDate] = useState(availableDates[0] || '');
    const [targetDate, setTargetDate] = useState('');
    const [copying, setCopying] = useState(false);

    const handleCopy = async () => {
        if (!sourceDate || !targetDate) return;
        setCopying(true);
        try {
            await onCopy(sourceDate, targetDate);
            onClose();
        } finally {
            setCopying(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
                <div className="flex items-center justify-between p-4 border-b">
                    <h3 className="text-lg font-semibold">Copy Entries</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-4 space-y-4">
                    <p className="text-sm text-gray-600">
                        Copy all manual entries from one date to another for <strong>{companyCode.toUpperCase()}</strong>.
                    </p>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Copy from date</label>
                        {availableDates.length > 0 ? (
                            <select
                                value={sourceDate}
                                onChange={e => setSourceDate(e.target.value)}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                {availableDates.map(d => (
                                    <option key={d} value={d}>
                                        {new Date(d).toLocaleDateString('de-CH')}
                                    </option>
                                ))}
                            </select>
                        ) : (
                            <p className="text-sm text-gray-400 italic">No dates with manual entries found</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Copy to date</label>
                        <input
                            type="date"
                            value={targetDate}
                            onChange={e => setTargetDate(e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                </div>

                <div className="flex justify-end gap-2 p-4 border-t">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleCopy}
                        disabled={!sourceDate || !targetDate || copying}
                        className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                        {copying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}
                        {copying ? 'Copying...' : 'Copy Entries'}
                    </button>
                </div>
            </div>
        </div>
    );
}
